import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Search, Users, CirclePlus, Edit2, Trash2 } from "lucide-react";
import { getJUsersList, getUsersByRole, getTeamList, deleteTeamById, updateUserStatus } from "../../api/common";
import SubmitButton from "../../Components/Button";
import ConfirmBox from "../../Components/ConfirmBox";
import Alert from "../../Components/AlertPopup";
import AddEditUserModal from "../../Containers/UserManagement/AddEditUserModal";
import ViewTeamMembersModal from "../../Containers/UserManagement/ViewTeamMembersModal";
import EditTeamModal from "../../Containers/UserManagement/EditTeamModal";
import AddTeamModal from "../../Containers/UserManagement/AddTeamModal";
import Breadcrumb from "../../Components/Breadcrumb";
import { ERROR_MESSAGE } from "../../constants";
import { getDateFormatString } from "../../utils";
import PageContainer from "../PageContainer";
import styles from "./style.module.scss";

const breadcrumbItems = [
  { label: "Configuration", path: "#"},
  { label: "Administration",path: "#" },
  { label: "User Management" },
];

const AVATAR_COLORS = [
  "#7C3AED", "#059669", "#2563EB", "#EA580C",
  "#DC2626", "#D946EF", "#0891B2", "#CA8A04",
  "#4F46E5", "#0D9488",
];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const TOP_TABS = ["Users", "Teams"];
const ROLE_FILTERS = ["All", "Admin", "Lead Auditor", "Auditor"];

const ROLE_FILTER_TO_API: Record<string, string> = {
  Admin: "ADMIN",
  "Lead Auditor": "LEAD_AUDITOR",
  Auditor: "AUDITOR",
};

const ROLE_BADGE_COLORS: Record<string, { bg: string; border: string; color: string }> = {
  "Lead Auditor": { bg: "#EFEAF6", border: "#102250", color: "#102250" } ,
  LEAD_AUDITOR:  { bg: "#EFEAF6", border: "#102250", color: "#102250" },
  Auditor: { bg: "#FFF4EA", border: "#EA580C", color: "#EA580C" },
  AUDITOR: { bg: "#FFF4EA", border: "#EA580C", color: "#EA580C" },
  Admin: { bg: "#FFF4FA", border: "#B45309", color: "#B45309" },
  ADMIN: { bg: "#FFF4FA", border: "#B45309", color: "#B45309" },
};

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  LEAD_AUDITOR: "Lead Auditor",
  AUDITOR: "Auditor",
  ADMIN: "Admin",
};

const getTeamMembers = (team: any) => {
  const leads = (team.leadAuditors || []).map((m: any) => ({
    ...m,
    role: "Lead Auditor",
  }));
  const auditors = (team.auditors || []).map((m: any) => ({
    ...m,
    role: "Auditor",
  }));
  return [...leads, ...auditors];
};

const UserManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Users");
  const [userList, setUserList] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editUser, setEditUser] = useState<any>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [teamList, setTeamList] = useState<any[]>([]);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [hoveredTeam, setHoveredTeam] = useState<number | null>(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTeam, setDeleteTeam] = useState<any>(null);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editTeamData, setEditTeamData] = useState<any>(null);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [statusToggleUser, setStatusToggleUser] = useState<any>(null);

  useEffect(() => {
    if (activeTab === "Users") {
      fetchUsersList();
    } else if (activeTab === "Teams") {
      fetchTeamsList();
    }
  }, [activeTab]);

  const fetchUsersList = async () => {
    try {
      const response = await getJUsersList();
      if (response && response.length > 0) {
        setUserList(
          response.map((user: any) => ({
            ...user,
            isActive: user.isActive !== undefined ? user.isActive : true,
          }))
        );
      }
    } catch {
      setShowErrorToast(true);
    }
  };

  const fetchUsersByRole = async (roleType: string) => {
    try {
      const response = await getUsersByRole(roleType);
      if (response && Array.isArray(response)) {
        setUserList(
          response.map((user: any) => ({
            ...user,
            isActive: user.isActive !== undefined ? user.isActive : true,
          }))
        );
      } else {
        setUserList([]);
      }
    } catch {
      setShowErrorToast(true);
    }
  };

  const fetchTeamsList = async () => {
    try {
      const response = await getTeamList();
      if (response && Array.isArray(response)) {
        setTeamList(response);
      }
    } catch {
      setShowErrorToast(true);
    }
  };

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
    if (filter === "All") {
      fetchUsersList();
    } else {
      const apiRoleType = ROLE_FILTER_TO_API[filter];
      if (apiRoleType) {
        fetchUsersByRole(apiRoleType);
      }
    }
  };

  const filteredUsers = useMemo(() => {
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      return userList.filter(
        (user) =>
          user.name?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term)
      );
    }
    return userList;
  }, [userList, searchTerm]);

  const filteredTeams = useMemo(() => {
    if (!teamSearchTerm.trim()) return teamList;
    const term = teamSearchTerm.toLowerCase();
    return teamList.filter((team) =>
      team.teamName?.toLowerCase().includes(term)
    );
  }, [teamList, teamSearchTerm]);

  const handleToggleActive = (userId: number) => {
    const user = userList.find((u) => u.userId === userId);
    if (!user) return;
    setStatusToggleUser(user);
    setShowStatusConfirm(true);
  };

  const handleStatusConfirm = async () => {
    if (!statusToggleUser) return;
    const newStatus = !statusToggleUser.isActive;
    try {
      await updateUserStatus(statusToggleUser.userId, newStatus);
      setUserList((prev) =>
        prev.map((u) =>
          u.userId === statusToggleUser.userId ? { ...u, isActive: newStatus } : u
        )
      );
    } catch {
      // API failed, don't update UI
    } finally {
      setShowStatusConfirm(false);
      setStatusToggleUser(null);
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setEditUser(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditUser(null);
    fetchUsersList();
  };

  const getRoleCounts = (team: any) => {
    const leadCount = (team.leadAuditors || []).length;
    const auditorCount = (team.auditors || []).length;
    const parts = [];
    if (leadCount > 0) parts.push(`${leadCount} Lead Auditor(s)`);
    if (auditorCount > 0) parts.push(`${auditorCount} Auditor(s)`);
    return parts.join(", ");
  };

  const handleDeleteTeamClick = (team: any) => {
    setDeleteTeam(team);
    setShowDeleteConfirm(true);
  };

  const handleDeleteTeamConfirm = async () => {
    if (!deleteTeam) return;
    try {
      await deleteTeamById(deleteTeam.teamId);
      setSuccessMessage("Team deleted successfully");
      setShowSuccessToast(true);
      fetchTeamsList();
    } catch {
      setShowErrorToast(true);
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTeam(null);
    }
  };

  const handleEditTeamClick = (team: any) => {
    setEditTeamData(team);
    setShowEditTeamModal(true);
  };

  const handleViewTeam = (team: any) => {
    setSelectedTeam(team);
    setShowTeamModal(true);
  };

  const handleTeamSaveSuccess = () => {
    fetchTeamsList();
    setSuccessMessage("Team saved successfully!");
    setShowSuccessToast(true);
  };

  const getRoleBadge = (roleType: string) => {
    return ROLE_BADGE_COLORS[roleType] || {
      bg: "transparent",
      border: "#475569",
      color: "#475569",
    };
  };

  return (
    <PageContainer>
      <div className={styles.breadcrumbNoNav}>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className={styles.container}>
        {/* Top Row: Users/Teams tabs + action button */}
        <div className={styles.topRow}>
          <div className={styles.topTabs}>
            {TOP_TABS.map((tab) => (
              <button
                key={tab}
                className={`${styles.topTab} ${activeTab === tab ? styles.topTabActive : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          {activeTab === "Users" ? (
            <SubmitButton onClick={() => navigate("/administration/role-mapping")}>
              Map AD Groups
            </SubmitButton>
          ) : (
            <SubmitButton onClick={() => setShowAddTeamModal(true)}>
              <CirclePlus size={18} /> &nbsp; Add Team
            </SubmitButton>
          )}
        </div>

        {/* Users Tab Content */}
        {activeTab === "Users" && (
          <div className={styles.contentArea}>
            <div className={styles.filterRow}>
              <div className={styles.filterTabs}>
                {ROLE_FILTERS.map((filter) => (
                  <button
                    key={filter}
                    className={`${styles.filterTab} ${activeFilter === filter ? styles.filterTabActive : ""}`}
                    onClick={() => handleFilterClick(filter)}
                  >
                    {filter}
                  </button>
                ))}
              </div>
              <div className={styles.searchBox}>
                <Search size={16} className={styles.searchIcon} />
                <Form.Control
                  type="text"
                  placeholder="Search"
                  className={styles.searchInput}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                />
              </div>
            </div>

            {filteredUsers.length > 0 ? (
              <Row className={styles.cardGrid}>
                {filteredUsers.sort((a, b) => a.name.localeCompare(b.name)).map((user) => {
                  const avatarColor = getAvatarColor(user.name || "");
                  const badge = getRoleBadge(user.roleType);

                  return (
                    <Col key={user.userId} xs={12} md={6} lg={3} className="mb-3">
                      <div
                        className={styles.userCard}
                        style={{
                          '--hover-shadow-color': badge.border
                        } as React.CSSProperties}
                      >
                        <div className={styles.cardTopRow}>
                          <div
                            className={styles.avatar}
                            style={{ backgroundColor: avatarColor }}
                          >
                            {(user.name || "U").split(" ").map((w: string) => w.charAt(0).toUpperCase()).slice(0, 2).join("")}
                          </div>
                          <span className={styles.userName}>{user.name}</span>
                          <span
                            className={styles.roleBadge}
                            style={{
                              backgroundColor: badge.bg,
                              borderColor: badge.border,
                              color: badge.color,
                            }}
                          >
                            {ROLE_DISPLAY_NAMES[user.roleType] || user.roleType}
                          </span>
                        </div>

                        <div className={styles.cardDetailRow}>
                          <span className={styles.detailText}>
                            <span className={styles.detailLabel}>Username:</span> {user.userName || user.name?.toLowerCase().replace(/\s+/g, "")}
                            <br />
                            <span className={styles.detailLabel}>Email:</span> {user.email}
                          </span>
                        </div>

                        <div className={styles.cardDetailRow}>
                          <span className={styles.detailText}>
                            <span className={styles.detailLabel}>Team:</span> {user.team || "Financial Audit"}
                          </span>
                          <div className={styles.toggleWrap}>
                            <span className={user.isActive ? styles.activeLabel : styles.inactiveLabel}>
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                            <Form.Check
                              type="switch"
                              id={`switch-${user.userId}`}
                              checked={user.isActive}
                              onChange={() => handleToggleActive(user.userId)}
                              className={styles.activeToggle}
                            />
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <div className={styles.emptyState}>
                <Users size={48} className={styles.emptyIcon} />
                <p className={styles.emptyTitle}>No {activeFilter !== "All" ? activeFilter : ""} Users Found</p>
                <p className={styles.emptySubtitle}>
                  {activeFilter !== "All"
                    ? `There are no users with the "${activeFilter}" role.`
                    : "No users available."}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Teams Tab Content */}
        {activeTab === "Teams" && (
          <div className={styles.contentArea}>
            <div className={styles.teamSearchRow}>
              <div className={styles.searchBox}>
                <Search size={16} className={styles.searchIcon} />
                <Form.Control
                  type="text"
                  placeholder="Search Team"
                  className={styles.searchInput}
                  value={teamSearchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setTeamSearchTerm(e.target.value)
                  }
                />
              </div>
            </div>

            {filteredTeams.length > 0 ? (
              <Row className={styles.cardGrid}>
                {filteredTeams.map((team) => {
                  const isHovered = hoveredTeam === team.teamId;
                  const allMembers = getTeamMembers(team);

                  return (
                    <Col key={team.teamId} xs={12} md={6} lg={4} className="mb-3">
                      <div
                        className={styles.teamCard}
                        onMouseEnter={() => setHoveredTeam(team.teamId)}
                        onMouseLeave={() => setHoveredTeam(null)}
                      >
                        {isHovered && (
                          <div className={styles.hoverActions}>
                            <button
                              className={styles.actionBtn}
                              title="Edit"
                              onClick={() => handleEditTeamClick(team)}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              className={styles.actionBtn}
                              title="Delete"
                              onClick={() => handleDeleteTeamClick(team)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}

                        <div className={styles.teamName}>{team.teamName}</div>

                        <div className={styles.teamAvatarRow}>
                          {allMembers.slice(0, 4).map((member: any) => (
                            <div
                              key={member.userId}
                              className={styles.teamAvatarCircle}
                              style={{ backgroundColor: getAvatarColor(member.name || "") }}
                              title={member.name}
                            >
                              {(member.name || "U").split(" ").map((w: string) => w.charAt(0).toUpperCase()).slice(0, 2).join("")}
                            </div>
                          ))}
                          {allMembers.length > 4 && (
                            <div
                              className={styles.teamAvatarCircle}
                              style={{ backgroundColor: "#6b7280" }}
                            >
                              +{allMembers.length - 4}
                            </div>
                          )}
                        </div>

                        <div className={styles.teamMembersRow}>
                          <Users size={16} className={styles.teamMembersIcon} />
                          <span className={styles.teamMembersCount}>{allMembers.length} Members</span>
                        </div>

                        <div className={styles.teamRoleCounts}>
                          {getRoleCounts(team)}
                        </div>

                        <div className={styles.teamInfoRow}>
                          <span className={styles.teamInfoLabel}>Created</span>
                          <div className={styles.teamInfoRight}>
                            <span className={styles.teamInfoDate}>{team.createdDate ? getDateFormatString(team.createdDate, "DD/MM/YYYY, h.mma") : "-"}</span>
                            <span className={styles.teamInfoBy}>{team.createdBy ? `by ${team.createdBy}` : ""}</span>
                          </div>
                        </div>

                        <div className={styles.teamInfoRow}>
                          <span className={styles.teamInfoLabel}>Updated</span>
                          <div className={styles.teamInfoRight}>
                            <span className={styles.teamInfoDate}>{team.modifiedDate ? getDateFormatString(team.modifiedDate, "DD/MM/YYYY, h.mma") : "Not updated yet"}</span>
                            <span className={styles.teamInfoBy}>{team.modifiedBy ? `by ${team.modifiedBy}` : ""}</span>
                          </div>
                        </div>

                        <button
                          className={styles.viewTeamBtn}
                          onClick={() => handleViewTeam(team)}
                        >
                          View Team
                        </button>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <div className={styles.emptyState}>
                <Users size={48} className={styles.emptyIcon} />
                <p className={styles.emptyTitle}>No Teams Found</p>
                <p className={styles.emptySubtitle}>No teams match your search.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <Alert
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={successMessage}
      />
      <Alert
        type="error"
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={ERROR_MESSAGE.API_ERROR_MESSAGE}
      />
      <AddEditUserModal
        show={showModal}
        onClose={closeModal}
        mode={modalMode}
        userData={editUser}
      />
      <ViewTeamMembersModal
        show={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        team={selectedTeam}
      />
      <EditTeamModal
        show={showEditTeamModal}
        onClose={() => setShowEditTeamModal(false)}
        team={editTeamData}
        onSaveSuccess={handleTeamSaveSuccess}
      />
      <AddTeamModal
        show={showAddTeamModal}
        onClose={() => setShowAddTeamModal(false)}
        onSaveSuccess={handleTeamSaveSuccess}
      />
      <ConfirmBox
        title="Confirm Delete"
        message={`Are you sure you want to delete "${deleteTeam?.teamName || ""}"?`}
        handleClose={() => setShowDeleteConfirm(false)}
        show={showDeleteConfirm}
        onConfirm={handleDeleteTeamConfirm}
        cancelText="Cancel"
        confirmText="Confirm"
        cancelClassName={styles.cancelBtn}
        confirmClassName={styles.deleteBtn}
        centered
      />
      <ConfirmBox
        title="Confirm Status Change"
        titleClass="mx-1"
        message={`Are you sure you want to ${statusToggleUser?.isActive ? "deactivate" : "activate"} "${statusToggleUser?.name || ""}"?`}
        handleClose={() => { setShowStatusConfirm(false); setStatusToggleUser(null); }}
        show={showStatusConfirm}
        onConfirm={handleStatusConfirm}
        cancelText="Cancel"
        confirmText="Confirm"
        centered
      />
    </PageContainer>
  );
};

export default UserManagement;